import React, { useState, useEffect } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { 
  Bell, 
  BellOff, 
  Check, 
  X, 
  Send, 
  Settings2,
  Users,
  Copy,
  Bot,
  TrendingUp,
  Wallet,
  DollarSign,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

export default function NotificationSettings() {
  const { 
    permission, 
    isSubscribed, 
    preferences,
    loading,
    requestPermission, 
    unsubscribeFromPush,
    updatePreferences,
    sendTestNotification
  } = useNotifications();

  const [localPrefs, setLocalPrefs] = useState({
    trade_alerts: true,
    follower_alerts: true,
    copy_alerts: true,
    copy_trade_alerts: true,
    ai_trade_alerts: true,
    price_alerts: true,
    deposit_alerts: true,
    withdrawal_alerts: true
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (preferences) {
      setLocalPrefs(preferences);
    }
  }, [preferences]);

  const handleToggle = (key) => {
    setLocalPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setSaving(true);
    await updatePreferences(localPrefs);
    setSaving(false);
  };

  const handleTestNotification = async () => {
    setTesting(true);
    await sendTestNotification();
    setTesting(false);
  };

  const preferenceItems = [
    { key: 'trade_alerts', label: 'Trade Alerts', description: 'When traders you follow make trades', icon: TrendingUp, color: 'text-green-400' },
    { key: 'follower_alerts', label: 'New Followers', description: 'When someone starts following you', icon: Users, color: 'text-blue-400' },
    { key: 'copy_alerts', label: 'Copy Trade Alerts', description: 'When someone copies your trades', icon: Copy, color: 'text-purple-400' },
    { key: 'copy_trade_alerts', label: 'Copied Trade Execution', description: 'When a copied trade is executed', icon: Copy, color: 'text-pink-400' },
    { key: 'ai_trade_alerts', label: 'AI Trade Alerts', description: 'When AI executes trades for you', icon: Bot, color: 'text-indigo-400' },
    { key: 'price_alerts', label: 'Price Alerts', description: 'When assets hit your target prices', icon: AlertTriangle, color: 'text-yellow-400' },
    { key: 'deposit_alerts', label: 'Deposit Alerts', description: 'When deposits are approved', icon: Wallet, color: 'text-emerald-400' },
    { key: 'withdrawal_alerts', label: 'Withdrawal Alerts', description: 'When withdrawals are processed', icon: DollarSign, color: 'text-orange-400' }
  ];

  if (loading) {
    return (
      <div className="bg-[#1A1A1A] rounded-xl p-6 border border-gray-800">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-[#00E676] animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#1A1A1A] rounded-xl border border-gray-800 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#00E676]/10 rounded-lg">
              <Bell className="w-5 h-5 text-[#00E676]" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Push Notifications</h3>
              <p className="text-gray-400 text-sm">Get alerts for trades, followers, and more</p>
            </div>
          </div>
          
          {/* Enable/Disable Toggle */}
          <div className="flex items-center gap-3">
            {permission === 'denied' && (
              <span className="text-red-400 text-sm flex items-center gap-1">
                <BellOff className="w-4 h-4" />
                Blocked by browser
              </span>
            )}
            
            {!isSubscribed ? (
              <button
                onClick={requestPermission}
                disabled={permission === 'denied'}
                className="px-4 py-2 bg-[#00E676] text-black rounded-lg font-medium hover:bg-[#00C853] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="enable-notifications-btn"
              >
                <Bell className="w-4 h-4" />
                Enable Notifications
              </button>
            ) : (
              <button
                onClick={unsubscribeFromPush}
                className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg font-medium hover:bg-gray-600 transition-colors flex items-center gap-2"
                data-testid="disable-notifications-btn"
              >
                <BellOff className="w-4 h-4" />
                Disable
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Status Banner */}
      {isSubscribed && (
        <div className="px-6 py-3 bg-[#00E676]/10 border-b border-[#00E676]/20 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[#00E676]">
            <Check className="w-4 h-4" />
            <span className="text-sm font-medium">Notifications enabled</span>
          </div>
          <button
            onClick={handleTestNotification}
            disabled={testing}
            className="text-sm text-[#00E676] hover:underline flex items-center gap-1"
            data-testid="test-notification-btn"
          >
            {testing ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Send className="w-3 h-3" />
            )}
            Send Test
          </button>
        </div>
      )}

      {/* Preferences */}
      {isSubscribed && (
        <div className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Settings2 className="w-4 h-4 text-gray-400" />
            <h4 className="text-white font-medium">Notification Preferences</h4>
          </div>
          
          <div className="space-y-3">
            {preferenceItems.map((item) => (
              <div 
                key={item.key}
                className="flex items-center justify-between p-3 bg-[#111111] rounded-lg hover:bg-[#151515] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <item.icon className={`w-5 h-5 ${item.color}`} />
                  <div>
                    <p className="text-white font-medium">{item.label}</p>
                    <p className="text-gray-500 text-sm">{item.description}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleToggle(item.key)}
                  className={`w-12 h-6 rounded-full transition-colors relative ${
                    localPrefs[item.key] ? 'bg-[#00E676]' : 'bg-gray-600'
                  }`}
                  data-testid={`toggle-${item.key}`}
                >
                  <div 
                    className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${
                      localPrefs[item.key] ? 'translate-x-6' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
          
          {/* Save Button */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-[#00E676] text-black rounded-lg font-medium hover:bg-[#00C853] transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="save-preferences-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              Save Preferences
            </button>
          </div>
        </div>
      )}

      {/* Not Subscribed State */}
      {!isSubscribed && permission !== 'denied' && (
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <BellOff className="w-8 h-8 text-gray-500" />
          </div>
          <h4 className="text-white font-medium mb-2">Stay Updated</h4>
          <p className="text-gray-400 text-sm mb-4 max-w-md mx-auto">
            Enable push notifications to get instant alerts when traders you follow make profitable trades, 
            when someone starts copying you, or when your deposits are approved.
          </p>
          <button
            onClick={requestPermission}
            className="px-6 py-2 bg-[#00E676] text-black rounded-lg font-medium hover:bg-[#00C853] transition-colors"
          >
            Enable Notifications
          </button>
        </div>
      )}

      {/* Blocked State */}
      {permission === 'denied' && (
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <BellOff className="w-8 h-8 text-red-400" />
          </div>
          <h4 className="text-white font-medium mb-2">Notifications Blocked</h4>
          <p className="text-gray-400 text-sm max-w-md mx-auto">
            You've blocked notifications for this site. To enable them, click the lock icon in your browser's 
            address bar and allow notifications, then refresh the page.
          </p>
        </div>
      )}
    </div>
  );
}
